import React from 'react'
import SelectComponent from '@Components/Selectors/Select'
import { ICONS } from '@Const/common'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { observer } from 'mobx-react'
import { useFormContext } from 'react-hook-form'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import AutoCompleteInputForm from '@Fields/v2/AutoCompleteInputForm'
import { FormFieldTypes } from '@App/enum/form-field-types'
import ToggleLabel from '@Components/Selectors/ToggleLabel'

interface CriterionRowProps<T extends object> {
    criterion: SearchCriterion<T>
    searchCriteriaStore: SearchCriteriaStore<T>
    index: number
    disableAutoComplete?: boolean
}

const CriterionRow = <T extends object>({
    criterion,
    searchCriteriaStore,
    index,
    disableAutoComplete,
}: CriterionRowProps<T>) => {
    const { register, setValue } = useFormContext()

    const name = 'search-with-criteria-select-' + criterion.id
    const optionsDiv = 'search-with-criteria-select-div-' + criterion.id
    const label = 'SearchWithCriteria' + criterion.id
    const criteriaOptions = searchCriteriaStore.getOptionsForCriterion(criterion)
    const inputName = `search.${index}.searchTerm` as const

    const toggleCriterionExactMatch = (checked: boolean) => {
        setValue(`search.${index}.exactMatchOnly`, checked, { shouldDirty: true })
        searchCriteriaStore.setCriterionExactMatch(index, checked)
    }

    return (
        <div className="search-pair" key={criterion.id} data-testid={optionsDiv}>
            <div className="search-select-holder">
                {/* This label is needed for testing*/}
                <label hidden htmlFor={name}>
                    {label}
                </label>
                <SelectComponent
                    name={name}
                    options={criteriaOptions}
                    selectedValue={criterion.selectedOption}
                    isDisabled={criterion.isDisabled}
                    onValueChange={(selectedOption) =>
                        searchCriteriaStore.setCriterionOption(index, selectedOption as SelectOptionHide<T>)
                    }
                />
            </div>
            <div className="search-input-holder">
                {!disableAutoComplete && criterion.selectedOption.autoCompleteFetcher ? (
                    <AutoCompleteInputForm
                        key={criterion.id}
                        name={inputName}
                        label={label}
                        type={FormFieldTypes.AUTOCOMPLETE}
                        autoCompleteFetcher={criterion.selectedOption.autoCompleteFetcher}
                        autoCompleteField={criterion.selectedOption.value}
                    />
                ) : (
                    <input
                        key={criterion.id}
                        {...register(inputName)}
                        data-testid={inputName}
                        className="input-no-autocomplete"
                    />
                )}
            </div>
            {!criterion.isFixed && (
                <img
                    src={ICONS.trashCan}
                    data-testid={'delete-criterion-' + criterion.selectedOption.id}
                    onClick={() => searchCriteriaStore.deleteCriterion(index)}
                    alt="Delete Criterion"
                />
            )}
            {criterion.selectedOption.exactMatchOnly && (
                <ToggleLabel
                    mainColor
                    onClick={toggleCriterionExactMatch}
                    label="Exact Match"
                    checked={criterion.exactMatchOnly}
                />
            )}
        </div>
    )
}

export default observer(CriterionRow)
